import { useCallback, useEffect, useRef, useState } from 'react';

export const VOICE_TRIGGERS = {
  MEDIATOR_ON_START:  'mediatorOnStart',
  EMPOWERING_CLOSURE: 'empowering-closure',
  MEDIATOR_ON_FAIL:   'mediatorOnFail',
};

const extractOralText = (description = '') => {
  const match = description.match(/«\s*([\s\S]+?)\s*»/);
  return match ? match[1].trim() : description.trim();
};

// Sélectionne la meilleure voix française disponible sur l'appareil
// Ordre de préférence : voix "premium" macOS/iOS → voix standard → fallback
const getBestFrenchVoice = () => {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;

  const frenchVoices = voices.filter(v => v.lang.startsWith('fr'));
  if (!frenchVoices.length) return null;

  // Ordre de préférence par nom (macOS "Thomas"/"Amélie" sont les meilleures)
  const preferred = ['Thomas', 'Amélie', 'Ameline', 'Audrey', 'Jacques'];
  for (const name of preferred) {
    const found = frenchVoices.find(v => v.name.includes(name));
    if (found) return found;
  }

  // Voix locale (non réseau) en priorité sur les voix cloud
  const local = frenchVoices.find(v => !v.localService === false);
  return local || frenchVoices[0];
};

export const useVoiceNarrator = ({
  onPlayStart = null,
  onPlayEnd = null,
} = {}) => {

  // analyserRef reste null — le K2000 utilisera son animation synthétique
  const analyserRef    = useRef(null);
  const utteranceRef   = useRef(null);

  const [isPlaying, setIsPlaying]       = useState(false);
  const [isLoading, setIsLoading]       = useState(false);
  const [currentTrigger, setCurrentTrigger] = useState(null);
  const [voiceReady, setVoiceReady]     = useState(false);

  // Les voix sont chargées de manière asynchrone — on attend qu'elles soient prêtes
  useEffect(() => {
    const checkVoices = () => {
      if (window.speechSynthesis.getVoices().length > 0) setVoiceReady(true);
    };
    checkVoices();
    window.speechSynthesis.addEventListener('voiceschanged', checkVoices);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', checkVoices);
  }, []);

  // Cleanup à l'unmount
  useEffect(() => {
    return () => { window.speechSynthesis.cancel(); };
  }, []);

  const stopNarration = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setCurrentTrigger(null);
  }, []);

  const narrate = useCallback(async (trigger, data) => {
    if (!data || isLoading || isPlaying) return;
    if (!window.speechSynthesis) {
      console.warn('[VoiceNarrator] SpeechSynthesis non supporté');
      return;
    }

    const rawText  = data.description || data.text || '';
    const oralText = extractOralText(rawText);
    if (oralText.length < 10) return;

    setIsLoading(true);
    window.speechSynthesis.cancel(); // Annuler toute lecture en cours

    const utterance = new SpeechSynthesisUtterance(oralText);
    utteranceRef.current = utterance;

    // Configuration voix
    const voice = getBestFrenchVoice();
    if (voice) utterance.voice = voice;

    utterance.lang  = 'fr-FR';
    utterance.rate  = 0.88;   // Légèrement ralenti → plus posé, moins robotique
    utterance.pitch = 0.95;   // Légèrement grave → plus de chaleur
    utterance.volume = 1.0;

    utterance.onstart = () => {
      setIsLoading(false);
      setIsPlaying(true);
      setCurrentTrigger(trigger);
      onPlayStart?.({ triggerType: trigger });
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setCurrentTrigger(null);
      onPlayEnd?.({ triggerType: trigger });
    };

    utterance.onerror = (e) => {
      // 'interrupted' est déclenché par cancel() — pas une vraie erreur
      if (e.error !== 'interrupted') {
        console.warn('[VoiceNarrator] SpeechSynthesis erreur:', e.error);
      }
      setIsLoading(false);
      setIsPlaying(false);
      setCurrentTrigger(null);
    };

    setIsLoading(true);
    window.speechSynthesis.speak(utterance);

  }, [isLoading, isPlaying, onPlayStart, onPlayEnd]);

  return {
    analyserRef,      // null → K2000 en mode synthétique
    isPlaying,
    isLoading,
    currentTrigger,
    voiceReady,       // utile pour désactiver le bouton si les voix ne sont pas encore chargées
    narrate,
    stopNarration,
  };
};